/* eslint-disable @typescript-eslint/no-explicit-any */
// input-factory.tsx
'use client'

import { FieldPath, FieldValues } from 'react-hook-form'
import { withLabel, WithLabelProps } from './with-label'
import { InputFieldProps, withReactHookForm } from './with-react-hook-form'

type Decoration = 'label' | 'form'

const hocMap: Record<
  Decoration,
  <T extends object>(
    Component: React.ComponentType<T>,
  ) => React.ComponentType<any>
> = {
  label: withLabel,
  form: withReactHookForm,
}

// Map each decorator to its props
type DecorationProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  label: WithLabelProps
  form: InputFieldProps<TFieldValues, TName>
}

type CollectProps<
  Ds extends Decoration[],
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Ds extends [infer D, ...infer Rest]
  ? D extends Decoration
    ? Rest extends Decoration[]
      ? DecorationProps<TFieldValues, TName>[D] &
          CollectProps<Rest, TFieldValues, TName>
      : DecorationProps<TFieldValues, TName>[D]
    : unknown
  : unknown

export function createInput<
  T extends object,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Ds extends Decoration[] = [],
>(Component: React.ComponentType<T>, decorations: [...Ds]) {
  let Comp: React.ComponentType<any> = Component
  for (const decorator of decorations) {
    Comp = hocMap[decorator](Comp)
  }

  return Comp as React.ComponentType<T & CollectProps<Ds, TFieldValues, TName>>
}
